import useTranslator from "../../hooks/useTranslator";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import ProcessPassedModal from "../../pages/onboarding/ProcessPassedModal";
import { trackClarityEvent } from "../../Utils/clarityReady";
import constants from "../../Utils/constants";
import { useCookies } from "react-cookie";
import { Button, Icon, Modal, TextArea } from "../design";

const FreeTextModal = ({
  user,
  showFreeTextPopup,
  setShowFreeTextPopup,
  updateTenantData,
  loadFullInfo,
  isPopup,
  originalUser,
  isJoinedFromLandlordLink,
  isDummySignup,
  setStep,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { loadUserTenant, setLoading } = useContext(AuthContext);
  const [aboutMe, setAboutMe] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [doItLaterLoading, setDoItLaterLoading] = useState(false);
  const [, setCookie] = useCookies(["joinGroupOnLandingPopup"]);
  const [showTickScreen, setShowTickScreen] = useState(false);

  useEffect(() => {
    setAboutMe(user?.about_me ?? "");
  }, [user]);

  const handleSubmitHandler = async () => {
    try {
      setSubmitLoading(true);

      let payload = {
        about_me: aboutMe,
        role: localStorage.getItem("usertype"),
        lang: localStorage.getItem("i18nextLng"),
      };

      if (isJoinedFromLandlordLink) {
        payload.is_edit = false;
        payload.profile_completed = true;

        setCookie("joinGroupOnLandingPopup", "1", { path: "/" });
        await updateTenantData(payload);
        navigate(`/home?welcome=true`);
      } else {
        payload.step = 9;
        await updateTenantData(payload);
      }

      if (isPopup) {
        await loadFullInfo(false);
        setShowFreeTextPopup(false);
      } else {
        trackClarityEvent("tenant_context_step_ended");
        setTimeout(() => {
          trackClarityEvent("tenant_onboarding_ended");
        }, constants.CLARITY_DELAY);
      }
    } catch (error) {
      console.log("Error submitting reason", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDoItLater = async () => {
    if (doItLaterLoading) {
      return;
    }
    // In dummy mode, just navigate without any API call
    if (isDummySignup) {
      sessionStorage.removeItem("dummy_email");
      sessionStorage.removeItem("isDummySignup");
      navigate(`/demo/home`);
      return;
    }

    let formData = {
      role: localStorage.getItem("usertype"),
      lang: localStorage.getItem("i18nextLng"),
    };

    if (isJoinedFromLandlordLink) {
      formData.is_edit = false;
      formData.profile_completed = true;

      setCookie("joinGroupOnLandingPopup", "1", { path: "/" });
    } else {
      formData.step = 9;
    }

    try {
      setDoItLaterLoading(true);
      await updateTenantData(formData);
      await loadUserTenant("tenant");

      if (isJoinedFromLandlordLink) {
        navigate(`/home?welcome=true`);
      }
      trackClarityEvent("tenant_onboarding_ended");
      setTimeout(() => {
        trackClarityEvent("tenant_profile_info_completed");
      }, constants.CLARITY_DELAY);
    } catch (err) {
    } finally {
      setDoItLaterLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={Boolean(showFreeTextPopup)}
        onClose={() => setShowFreeTextPopup(false)}
        dismissible={Boolean(isPopup)}
        title={_t("addContextToYourApplication")}
        description={_t("thisInformationHelpsUsReviewYourCaseFaster")}
        closeLabel={_t("close")}
        align="start"
        actionsLayout="end"
        actions={
          <>
            {!isPopup && (
              <Button
                variant="secondary"
                size="lg"
                className="ds-modal__actions-start"
                loading={doItLaterLoading}
                iconEnd={<Icon name="arrowRight" />}
                onClick={handleDoItLater}
              >
                {_t(
                  "iWillDoItLater",
                  "Text used in guarantor screen at onboarding flow"
                )}
              </Button>
            )}
            {isPopup && (
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setShowFreeTextPopup(false)}
              >
                {_t("cancel")}
              </Button>
            )}
            <Button
              type="submit"
              form="free-text-form"
              size="lg"
              loading={submitLoading}
            >
              {isPopup ? _t("save") : _t("continue")}
            </Button>
          </>
        }
      >
        <form
          id="free-text-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (isPopup) {
              handleSubmitHandler();
            } else {
              setShowTickScreen(true);
            }
          }}
        >
          <TextArea
            id="free-text-about-me"
            rows={6}
            placeholder={_t(
              "theMoreContextYouGiveUsTheFasterWeCanProcessYourApplication"
            )}
            maxLength={500}
            hint={`${aboutMe?.length ?? 0}/500`}
            value={aboutMe}
            onChange={(e) => setAboutMe(e.target.value)}
          />
        </form>
      </Modal>
      <ProcessPassedModal
        heading={_t("onboardingSuccessful")}
        subheading={_t("yourOnboardingHasBeenSuccessfullyCompleted")}
        showPopup={showTickScreen}
        buttonText={"goHome"}
        handleClick={handleSubmitHandler}
      />
    </>
  );
};

export default FreeTextModal;
